import React, { useState, useEffect, useMemo } from "react";
import { Groupper, Header, Icon, Button, Toast, Modal, Message, Table, Pagination, Toolbar, Input, Label } from "react-frontier";
import { useBreadcrumbs, useUser } from "../hooks";
import { EmploymentScheduleType, SetLoading, UserEmployment } from "../SiteClasses";
import { bindClose, addCommas, getCheckInStatus, getStatusColor, getStatusLabel } from "../SiteUtil";
import { Progress, Dropdown } from "semantic-ui-react";
import { Link } from "react-router-dom";
import Validator from "../Validator";
import UserAccess from "../UserAccess";
import API from "../API";
import moment from "moment";
import classnames from "classnames";


const PAGE_SIZE = 200;

enum Modals{
	INFO = 1,
	CHECK_IN = 2,
	CHECK_OUT = 3,
	EDIT = 4,
	REPORT = 5,
	REMOVE_CHECKOUT = 6,
}

var EmploymentDashboard = ()=>{
	var { setTitle } = useBreadcrumbs();
	var { hasAccess } = useUser();
	var [loadError, setLoadError] = useState<string>(null);
	var [employees, setEmployees] = useState<UserEmployment[]>(null);
	var [selectedEmployee, setSelectedEmployee] = useState<UserEmployment>(null);
	var [shownModal, setShownModal] = useState<Modals>(null);
	var [modalPrompts, setModalPrompts] = useState<string[]>([]);
	var [page, setPage] = useState<number>(0);
	var [selectedDate, setSelectedDate] = useState<number>(moment().startOf('day').unix());
	var [weekStart, setWeekStart] = useState<number>(moment().startOf('isoWeek').unix());
	var [search, setSearch] = useState<string>('');
	var [inputDate, setInputDate] = useState<number>(null);
	var [inputDateEnd, setInputDateEnd] = useState<number>(null);
	var [reportDateStart, setReportDateStart] = useState<number>(null);
	var [reportDateEnd, setReportDateEnd] = useState<number>(null);
	var [reportByDay, setReportByDay] = useState<boolean>(false);
	var [reportPrompts, setReportPrompts] = useState<string[]>([]);
	var [loading, setLoading] = useState<boolean>(false);

	var isToday = useMemo(() => moment.unix(selectedDate).isSame(moment(), 'day'), [selectedDate]);
	var canEdit = useMemo(() => hasAccess(UserAccess.EMPLOYMENTS.EDIT_EMPLOYMENTS), [hasAccess]);
	var sortedEmployees = useMemo(()=>{
		if(!employees) return [];
		var now = moment().unix();

		return [...employees].sort((a, b)=>{
			var aPendingLateCheckIn = !a.date_start && !a.log_type_id && a.type_id !== EmploymentScheduleType.REST && moment.unix(selectedDate).startOf('day').add(a.time_start, 'minutes').unix() < now;
			var bPendingLateCheckIn = !b.date_start && !b.log_type_id && b.type_id !== EmploymentScheduleType.REST && moment.unix(selectedDate).startOf('day').add(b.time_start, 'minutes').unix() < now;
			if(aPendingLateCheckIn === bPendingLateCheckIn) return 0;
			return aPendingLateCheckIn ? 1 : -1;
		});
	}, [employees, selectedDate]);

	var filteredEmployees = useMemo(()=>{
		var query = search.trim().toLowerCase();
		if(!query) return sortedEmployees;

		return sortedEmployees.filter(employee=>{
			var searchableText = [
				employee.name,
				employee.employment_name,
				employee.user_id,
				employee.employment_id,
			].filter(Boolean).join(' ').toLowerCase();
			return searchableText.includes(query);
		});
	}, [sortedEmployees, search]);

	var goToPreviousWeek = () => {
		var newWeekStart = moment.unix(weekStart).subtract(1, 'week').startOf('isoWeek').unix();
		setWeekStart(newWeekStart);
		setSelectedDate(newWeekStart);
	};

	var goToNextWeek = () => {
		var newWeekStart = moment.unix(weekStart).add(1, 'week').startOf('isoWeek').unix();
		setWeekStart(newWeekStart);
		setSelectedDate(newWeekStart);
	};

	useEffect(()=>{
		setTitle('Dashboard de Empleos');
		get();
	}, [selectedDate]);

	var get = (page: number = 0)=>{
		var offset = page * PAGE_SIZE;
		var date_start = selectedDate;
		var date_end = moment.unix(selectedDate).endOf('day').unix();

		setLoading(true);
		API.getActiveEmploymentWorkers(date_start, date_end, PAGE_SIZE, offset).then(res=>{
			if(res.error) return setLoadError(res.message);
			setEmployees(res.data);
			setPage(page);
		}).catch(()=>{
			setLoadError('Hubo un error cargando los empleados activos');
		}).finally(()=>{
			setLoading(false);
		});
	};

	var onPageChange = (newPage: number)=>{
		get(newPage);
	};

	var openReportModal = ()=>{
		setReportDateStart(null);
		setReportDateEnd(null);
		setReportByDay(false);
		setReportPrompts([]);
		setShownModal(Modals.REPORT);
	};

	var onSelect = (employee: UserEmployment)=>{
		if(!canEdit) return;
		setSelectedEmployee(employee);

		var defaultCheckInTime = moment.unix(selectedDate).startOf('day').add(employee.time_start, 'minutes').unix();
		var defaultCheckOutTime = moment.unix(selectedDate).startOf('day').add(employee.time_end, 'minutes').unix();

		setInputDate(employee.date_start || defaultCheckInTime);
		setInputDateEnd(employee.date_end || defaultCheckOutTime);
		setShownModal(Modals.INFO);
		setModalPrompts([]);
	};

	var onCheckIn = (setLoading: SetLoading)=>{
		var { valid, prompts } = Validator({ date: inputDate }, {
			date: [{
				rule: 'number', prompt: 'Favor de ingresar una fecha y hora válida',
			}],
		})
		setModalPrompts(prompts);
		if(!valid) return;

		setLoading(true);
		API.employmentCheckIn(selectedEmployee.user_id, selectedEmployee.schedule_id, inputDate).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Entrada registrada correctamente');

			setEmployees(employees.map(e =>
				e.user_id === selectedEmployee.user_id && e.schedule_id === selectedEmployee.schedule_id
					? { ...e, date_start: inputDate, log_id: res.data.log_id }
					: e
			));
			setShownModal(null);
		}).catch(()=>{
			setModalPrompts(['Hubo un error registrando la entrada']);
		}).finally(()=>{
			setLoading(false);
		});
	};

	var onCheckOut = (setLoading: SetLoading)=>{
		var { valid, prompts } = Validator({ date: inputDate }, {
			date: [{
				rule: 'number', prompt: 'Favor de ingresar una fecha y hora válida',
			}],
		})
		setModalPrompts(prompts);
		if(!valid) return;

		setLoading(true);
		API.employmentCheckOut(selectedEmployee.log_id, inputDate).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Salida registrada correctamente');

			setEmployees(employees.map(e =>
				e.user_id === selectedEmployee.user_id && e.schedule_id === selectedEmployee.schedule_id
					? { ...e, date_end: inputDate }
					: e
			));
			setShownModal(null);
		}).catch(()=>{
			setModalPrompts(['Hubo un error registrando la salida']);
		}).finally(()=>{
			setLoading(false);
		});
	};

	var onEdit = (setLoading: SetLoading)=>{
		var validationData: any = { date_start: inputDate };
		var validationRules: any = {
			date_start: [{
				rule: 'number', prompt: 'Favor de ingresar una fecha y hora de entrada válida',
			}],
		};
		if(selectedEmployee.date_end) {
			validationData.date_end = inputDateEnd;
			validationRules.date_end = [{
				rule: 'number', prompt: 'Favor de ingresar una fecha y hora de salida válida',
			}];
		}
		var { valid, prompts } = Validator(validationData, validationRules);
		setModalPrompts(prompts);
		if(!valid) return;

		setLoading(true);
		API.employmentEditCheckIn(selectedEmployee.log_id, inputDate, selectedEmployee.date_end ? inputDateEnd : 0).then(res=>{
			if(res.error) {
				setModalPrompts([res.message]);
			} else {
				Toast.success(selectedEmployee.date_end ? 'Entrada y salida actualizadas correctamente' : 'Entrada actualizada correctamente');

				setEmployees(employees.map(e =>
					e.user_id === selectedEmployee.user_id && e.schedule_id === selectedEmployee.schedule_id
						? { ...e, date_start: inputDate, ...(selectedEmployee.date_end ? { date_end: inputDateEnd } : {}) }
						: e
				));
				setShownModal(null);
			}
		}).catch(()=>{
			setModalPrompts(['Hubo un error actualizando los registros']);
		}).finally(()=>{
			setLoading(false);
		});
	};

	var onRemoveCheckOut = (setLoading: SetLoading)=>{
		if(!selectedEmployee) return;
		setLoading(true);
		API.employmentEditCheckIn(selectedEmployee.log_id, selectedEmployee.date_start, 0).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Salida eliminada correctamente');
			setSelectedEmployee({ ...selectedEmployee, date_end: 0 });
			setEmployees(employees.map(e =>
				e.user_id === selectedEmployee.user_id && e.schedule_id === selectedEmployee.schedule_id
					? { ...e, date_end: 0 }
					: e
			));
			setShownModal(Modals.INFO);
		}).catch(()=>{
			setModalPrompts(['Hubo un error eliminando la salida']);
		}).finally(()=>{
			setLoading(false);
		});
	};

	var downloadReport = (setLoading: SetLoading)=>{
		var { valid, prompts } = Validator({ reportDateStart, reportDateEnd }, {
			reportDateStart: [{ rule: 'number', label: 'Fecha inicio' }],
			reportDateEnd: [{ rule: 'number', label: 'Fecha fin' }],
		});
		setReportPrompts(prompts);
		if(!valid) return;
		if(reportDateEnd < reportDateStart) return setReportPrompts(['La fecha de fin no puede ser menor que la fecha de inicio.']);

		var dateStart = moment.unix(reportDateStart).startOf('day').unix();
		var dateEnd = moment.unix(reportDateEnd).endOf('day').unix();

		setLoading(true);
		API.downloadEmploymentAssistanceReport(dateStart, dateEnd, null, reportByDay).then(res=>{
			if(res.error) return setReportPrompts([res.message]);
			Toast.success('Reporte descargado correctamente');
			setShownModal(null);
		}).catch(()=>{
			setReportPrompts(['Hubo un error descargando el reporte. (EMP-RPT-1)']);
		}).finally(()=>{
			setLoading(false);
		});
	};

	if(loadError) return <Header text='Error' subtext={loadError} />
	if(!employees) return <Header text='Cargando datos' loading/>

	return <div className='ar employment-dashboard-screen'>
		<Groupper title={'Dashboard de empleados'} fitted width={850} style={{ marginTop: 20 }} titleRight={(
			<Dropdown icon={null} pointing direction='left' trigger={(
				<Button text='Opciones' color='black' iconRight='caret-down' iconRightStyle={{ marginRight: 0, marginLeft: 5 }} size='small'/>
			)}>
				<Dropdown.Menu>
					{hasAccess(UserAccess.EMPLOYMENTS.VIEW_EMPLOYMENTS) && (
						<Dropdown.Item style={{ marginBottom: 0 }} text='Ver asistencias' icon='eye' as={Link} to='/employments/assistance' />
					)}
					{hasAccess(UserAccess.EMPLOYMENTS.VIEW_EMPLOYMENTS) && (
						<Dropdown.Item style={{ marginBottom: 0 }} text='Descargar reporte' icon='download' onClick={openReportModal} />
					)}
				</Dropdown.Menu>
			</Dropdown>
		)}>
			<Groupper.Divider style={{
				padding: 10,
				display: 'flex',
				justifyContent: 'center',
				gap: 10,
				height: 80,
				marginBottom: 5,
			}}>
				<Button iconName="arrow-left" icon onClick={goToPreviousWeek}/>
				{Array.from({ length: 7 }).map((_, index) => {
					var date = moment.unix(weekStart).add(index, 'days');
					var isSelected = date.isSame(moment.unix(selectedDate), 'day');
					return (
						<>
							<div
								key={index}
								onClick={() => setSelectedDate(date.startOf('day').unix())}
								style={{
									display: 'flex',
									flexDirection: 'column',
									alignItems: 'center',
									padding: 10,
									borderRadius: 8,
									backgroundColor: isSelected ? 'orange' : 'white',
									cursor: 'pointer',
									minWidth: 95
								}}
							>
								<div style={{
									fontSize: 12,
									fontWeight: 'bold',
									color: isSelected ? 'white' : 'black',
									marginBottom: 2
								}}>
									{date.format('ddd')}
								</div>
								<div style={{
									fontSize: 20,
									fontWeight: 'bold',
									color: isSelected ? 'white' : 'black',
								}}>
									{date.format('DD')}
								</div>
							</div>
						</>
					);
				})}
				<Button iconName="arrow-right" icon onClick={goToNextWeek}/>
			</Groupper.Divider>
			<Input placeholder='Buscar...' value={search} onChange={setSearch} style={{ marginBottom: 15, marginTop: 15, paddingLeft: 15, paddingRight: 15 }} />
			{loading ? (
				<Header text="Cargando horarios..." loading />
			) : filteredEmployees.length > 0 ? (
				<div style={{ padding: 5 }}>
					{filteredEmployees.map(e=>{
						var canEdit = hasAccess(UserAccess.EMPLOYMENTS.EDIT_EMPLOYMENTS);
						var status = getCheckInStatus(e);
						var statusColor = getStatusColor(status);
						var statusLabel = getStatusLabel(status);
						var hasIncidence = !!e.log_type_id;
						var isRest = !hasIncidence && e.type_id === EmploymentScheduleType.REST;

						return (
						<div
							key={`employee-${e.user_id}-${e.employment_id}`}
							className={classnames('item', canEdit && 'clickable')}
							onClick={() => onSelect(e)}
						>
							<div className="avatar">
								<Icon name='user'/>
							</div>
							<div className="info">
								<div className="name">{e.employment_name}</div>
								<div className="employment">{e.name}</div>
							</div>
							<div className="status">
								<Label style={{ width: 100 }} color={statusColor}>
									{statusLabel}
								</Label>
							</div>
								<div className="schedule">
									<Progress
										style={{ minWidth: 150, marginTop: 15 }}
										percent={hasIncidence || isRest ? 100 : e.date_end ? 100 : e.date_start ? Math.min(100, Math.max(0, ((isToday ? moment().unix() : moment.unix(selectedDate).endOf('day').unix()) - e.date_start) / ((e.time_end - e.time_start) * 60) * 100)) : 0}
										size='tiny'
										color={statusColor}
										label={hasIncidence || isRest ? (hasIncidence ? (e.log_type_name || statusLabel) : statusLabel) : e.date_start ?
											moment.unix(e.date_start).format('HH:mm') + (e.date_end ? ' - ' + moment.unix(e.date_end).format('HH:mm') : ' - TBA')
											:
											moment.unix(selectedDate).add( e.time_start, 'minutes').format('HH:mm') + ' - ' + moment.unix(selectedDate).add(e.time_end, 'minutes').format('HH:mm')}
									/>
							</div>
						</div>
					);
				})}
				</div>
			) : (
				<Header
					text={'No se encontraron registros'}
					subtext={search.trim() ? 'No hubo coincidencias con la búsqueda actual.' : 'No se encontraron horarios asignados en esta fecha.'}
					iconName='clock-four'
					size="small"
					style={{ marginTop: 50, marginBottom: 20 }}
				/>
			)}
			{employees && (employees.length >= PAGE_SIZE || page !== 0) ? (
				<div style={{ display: 'flex', justifyContent: 'flex-end', padding: '15px', borderTop: '1px solid #e8e8e8' }}>
					<Pagination page={page} onPageChange={onPageChange} hasNext={employees.length >= PAGE_SIZE} />
				</div>
			) : null}
		</Groupper>
		<Modal open={shownModal === Modals.INFO} onClose={bindClose(setShownModal)} size="mini">
			{selectedEmployee && <>
				<Modal.Header>Información de asistencia</Modal.Header>
					<Modal.Content style={{ padding: 0 }}>
						<Toolbar fitted style={{ borderRadius: 0}}>
							{!selectedEmployee.log_id && !selectedEmployee.log_type_id && selectedEmployee.type_id !== EmploymentScheduleType.REST && (
							<Toolbar.Item text="Check-In" iconName='user-clock' onClick={()=>{
								var defaultCheckInTime = moment.unix(selectedDate).startOf('day').add(selectedEmployee.time_start, 'minutes').unix();
								setInputDate(defaultCheckInTime);
								setShownModal(Modals.CHECK_IN);
							}}/>
							)}
							{selectedEmployee.log_id && !selectedEmployee.log_type_id && selectedEmployee.type_id !== EmploymentScheduleType.REST && !selectedEmployee.date_end && (
							<Toolbar.Item text="Check-Out" iconName='user-check' onClick={()=>{
								var defaultCheckOutTime = moment.unix(selectedDate).startOf('day').add(selectedEmployee.time_end, 'minutes').unix();
								setInputDate(defaultCheckOutTime);
								setShownModal(Modals.CHECK_OUT);
							}}/>
							)}
							{selectedEmployee.log_id && !selectedEmployee.log_type_id && selectedEmployee.type_id !== EmploymentScheduleType.REST && (
								<Toolbar.Item text="Editar" iconName='edit' onClick={()=>{
									var defaultCheckInTime = moment.unix(selectedDate).startOf('day').add(selectedEmployee.time_start, 'minutes').unix();
									var defaultCheckOutTime = moment.unix(selectedDate).startOf('day').add(selectedEmployee.time_end, 'minutes').unix();
								setInputDate(selectedEmployee.date_start || defaultCheckInTime);
								setInputDateEnd(selectedEmployee.date_end || defaultCheckOutTime);
								setShownModal(Modals.EDIT);
							}}/>
						)}
						{selectedEmployee.log_id && !selectedEmployee.log_type_id && selectedEmployee.type_id !== EmploymentScheduleType.REST && !!selectedEmployee.date_end && (
							<Toolbar.Item text="Eliminar salida" iconName='clock-rotate-left' onClick={()=>{ setModalPrompts([]); setShownModal(Modals.REMOVE_CHECKOUT); }}/>
						)}
					</Toolbar>
					<Table details striped data={[
						['Empleado', selectedEmployee.name],
						['Empleo', <div>
							{selectedEmployee.employment_name}
							<Link to={`/employments/${selectedEmployee.employment_id}`} target="_blank" style={{ textDecoration: 'none', color: 'black'}}>
								<Icon name='external-link-alt' style={{ marginLeft: 5 }}/>
							</Link>
						</div>],
							['Salario', '$' + addCommas(selectedEmployee.salary)],
							['Agregado', moment.unix(selectedEmployee.date_created).format('DD/MM/YYYY HH:mm')],
							['Horario', `${moment.unix(selectedDate).add(selectedEmployee.time_start, 'minutes').format('HH:mm')} - ${moment.unix(selectedDate).add(selectedEmployee.time_end, 'minutes').format('HH:mm')}`],
							...(selectedEmployee.type_id === EmploymentScheduleType.REST && !selectedEmployee.log_type_id ? [['Tipo de horario', 'Descanso']] : []),
							...(selectedEmployee.log_type_id ? [['Tipo de registro', selectedEmployee.log_type_name || 'Incidencia']] : []),
							...(selectedEmployee.log_type_id ? [['Notas', selectedEmployee.notes || '-']] : []),
							...(!selectedEmployee.log_type_id && selectedEmployee.type_id !== EmploymentScheduleType.REST && selectedEmployee.date_start ? [['Entrada', moment.unix(selectedEmployee.date_start).format('HH:mm')]] : []),
							...(!selectedEmployee.log_type_id && selectedEmployee.type_id !== EmploymentScheduleType.REST && selectedEmployee.date_end ? [['Salida', moment.unix(selectedEmployee.date_end).format('HH:mm')]] : [])
						]}/>
					<Message type="error" list={modalPrompts} />
				</Modal.Content>
				<Modal.Actions>
					<Button text="Cerrar" basic onClick={bindClose(setShownModal, null)} />
				</Modal.Actions>
			</>}
		</Modal>
		<Modal open={shownModal === Modals.CHECK_IN || shownModal === Modals.CHECK_OUT} onClose={bindClose(setShownModal)} size="mini">
			<Modal.Header>{shownModal === Modals.CHECK_IN ? 'Registrar entrada' : 'Registrar salida'}</Modal.Header>
			<Modal.Content>
				<Input label={shownModal === Modals.CHECK_IN ? "Hora de entrada" : "Hora de salida"} value={inputDate} onChange={setInputDate} calendar={{
						date:  selectedDate,
						mode: 'datetime',
						format: 'HH:mm'
					}}
				/>
				<Message type="error" list={modalPrompts}/>
			</Modal.Content>
			<Modal.Actions>
				<Button text="Cancelar" basic onClick={bindClose(setShownModal, Modals.INFO)} />
				<Button text="Guardar" color="black" onClick={shownModal === Modals.CHECK_IN ? onCheckIn : onCheckOut} />
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal === Modals.EDIT} onClose={bindClose(setShownModal)} size="mini">
			<Modal.Header>{selectedEmployee?.date_end ? 'Editar entrada y salida' : 'Editar entrada'}</Modal.Header>
			<Modal.Content>
				<Input label="Hora de entrada" value={inputDate} onChange={setInputDate} calendar={{
						date:  selectedDate,
						mode: 'datetime',
						format: 'HH:mm'
					}}
				/>
				{selectedEmployee?.date_end && (
				<Input label="Hora de salida" value={inputDateEnd} onChange={setInputDateEnd} calendar={{
						date:  selectedDate,
						mode: 'datetime',
						format: 'HH:mm'
					}}
				/>
				)}
				<Message type="error" list={modalPrompts}/>
			</Modal.Content>
			<Modal.Actions>
				<Button text="Cancelar" basic onClick={bindClose(setShownModal, Modals.INFO)} />
				<Button text="Guardar" color="black" onClick={onEdit} />
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal === Modals.REMOVE_CHECKOUT} onClose={bindClose(setShownModal)} size="mini">
			<Modal.Header>Eliminar salida</Modal.Header>
			<Modal.Content>
				<Header text='¿Eliminar la salida de este registro?' subtext='El empleado continuará con el tiempo de trabajo abierto.' centered style={{ marginBottom: 15 }}/>
				<Message type="error" list={modalPrompts}/>
			</Modal.Content>
			<Modal.Actions>
				<Button text="Cancelar" basic onClick={bindClose(setShownModal, Modals.INFO)} />
				<Button text="Eliminar salida" color="red" onClick={onRemoveCheckOut} />
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal === Modals.REPORT} onClose={bindClose(setShownModal)} size="tiny">
			<Modal.Header>Reporte de asistencias</Modal.Header>
			<Modal.Content>
				<div style={{ display: 'grid', gap: 10 }}>
					<Input
						label='Fecha inicio'
						value={reportDateStart}
						onChange={setReportDateStart}
						style={{ width: '100%' }}
						calendar={{
							date: reportDateStart || moment().startOf('day').unix(),
							mode: 'date',
							format: 'DD/MM/YYYY',
						}}
					/>
					<Input
						label='Fecha fin'
						value={reportDateEnd}
						onChange={setReportDateEnd}
						style={{ width: '100%' }}
						calendar={{
							date: reportDateEnd || moment().endOf('day').unix(),
							mode: 'date',
							format: 'DD/MM/YYYY',
						}}
					/>
					<div>
						<div style={{ fontSize: 12, fontWeight: 'bold', marginBottom: 5 }}>Formato</div>
						<Dropdown
							fluid
							selection
							value={reportByDay}
							options={[
								{ value: false, text: 'Lista' },
								{ value: true, text: 'Por días' },
							]}
							onChange={(e, { value })=> setReportByDay(value === true)}
						/>
					</div>
				</div>
				<Message type="error" list={reportPrompts}/>
			</Modal.Content>
			<Modal.Actions>
				<Button text="Cancelar" basic onClick={bindClose(setShownModal, null)} />
				<Button text="Descargar" color="black" iconName='download' onClick={downloadReport} />
			</Modal.Actions>
		</Modal>
	</div>
}

export default EmploymentDashboard;
